// Current-time tracking for EMBEDDED video players so lesson notes can capture
// the video position. A direct-file <video> exposes currentTime to the page, but
// a cross-origin iframe does not — its position is only reachable through the
// provider's JS API:
//   • YouTube   → the IFrame Player API (window.YT)
//   • VdoCipher → its v2 API (player.video.currentTime)  ["flexi" / DRM]
//   • Vimeo / Gumlet / flexi_gumlet / any player.js-compatible embed → PlayerJs
//     (the Embedly player.js SDK). This is the SAME library the legacy learner
//     app (Course_Membership/GumletVdoplayer) uses, and it's what actually works
//     for these embeds — the library performs each provider's ready→subscribe
//     handshake, which a hand-rolled postMessage listener does not.
// Everything is best-effort: if a script fails to load or the embed isn't
// recognised, getSeconds() returns null and the caller omits the time.

const YT_API_SRC = "https://www.youtube.com/iframe_api";
const HLS_SRC = "https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js";
const VDO_API_SRC = "https://player.vdocipher.com/v2/api.js";
// Embedly player.js — exposes window.playerjs and drives Vimeo, Gumlet and any
// player.js receiver. This exact build is the one the legacy app ships.
const PLAYERJS_SRC = "https://cdn.embed.ly/player-0.1.0.min.js";

const scriptPromises = {};
function loadScriptOnce(src) {
    if (scriptPromises[src]) return scriptPromises[src];
    scriptPromises[src] = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`failed to load ${src}`));
        document.head.appendChild(s);
    });
    return scriptPromises[src];
}

// Resolve once window.YT.Player exists (the API calls a global when ready).
let ytReadyPromise;
function loadYouTubeApi() {
    if (ytReadyPromise) return ytReadyPromise;
    ytReadyPromise = new Promise((resolve, reject) => {
        if (window.YT && window.YT.Player) return resolve(window.YT);
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === "function") { try { prev(); } catch { /* ignore */ } }
            resolve(window.YT);
        };
        loadScriptOnce(YT_API_SRC).catch(reject);
    });
    return ytReadyPromise;
}

// Embedly player.js — resolves window.playerjs (or null if it can't load).
let playerjsPromise;
function loadPlayerjs() {
    if (window.playerjs) return Promise.resolve(window.playerjs);
    if (playerjsPromise) return playerjsPromise;
    playerjsPromise = loadScriptOnce(PLAYERJS_SRC).then(() => window.playerjs || null).catch(() => null);
    return playerjsPromise;
}

// Official Vimeo Player SDK — exposes window.Vimeo.Player. Embedly player.js
// does NOT reliably drive Vimeo, so Vimeo gets its own SDK (as the legacy app's
// vimeoVideoPlayer does via @vimeo/player).
const VIMEO_API_SRC = "https://player.vimeo.com/api/player.js";
let vimeoPromise;
function loadVimeo() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve(window.Vimeo);
    if (vimeoPromise) return vimeoPromise;
    vimeoPromise = loadScriptOnce(VIMEO_API_SRC).then(() => window.Vimeo || null).catch(() => null);
    return vimeoPromise;
}

// hls.js for .m3u8 streams (used by the HlsVideo <video> component). Resolves
// window.Hls, or null if it can't load.
export function loadHlsJs() {
    if (window.Hls) return Promise.resolve(window.Hls);
    return loadScriptOnce(HLS_SRC).then(() => window.Hls || null).catch(() => null);
}

// VdoCipher v2 API for DRM (flexi) embeds — exposes player.video (a media
// element) whose currentTime we can read, exactly like the old app.
// Exported because vdoCipher.js drives the same API for resume + progress, and
// the script must be loaded exactly once per page however many callers want it.
let vdoReadyPromise;
export function loadVdoCipherApi() {
    if (vdoReadyPromise) return vdoReadyPromise;
    vdoReadyPromise = loadScriptOnce(VDO_API_SRC).then(() => window.VdoPlayer || null).catch(() => null);
    return vdoReadyPromise;
}
const loadVdo = loadVdoCipherApi;

function providerOf(src) {
    const u = String(src || "");
    if (/youtube\.com\/embed|youtu\.be/i.test(u)) return "youtube";
    if (/player\.vdocipher\.com/i.test(u)) return "vdocipher";
    if (/player\.vimeo\.com|vimeo\.com/i.test(u)) return "vimeo";
    // Gumlet, flexi_gumlet and any other player.js-compatible embed are driven
    // through the Embedly player.js SDK (like the legacy GumletVdoplayer).
    return "playerjs";
}

/**
 * Attach a time tracker to an embed <iframe>. Returns { getSeconds, seekTo,
 * destroy }. The current position is cached from the provider's timeupdate
 * events, so getSeconds() is a cheap sync read (null until the player is ready).
 * The iframe must already carry enablejsapi=1 for YouTube (see embedUrl()).
 *
 * opts.onReady fires once, the moment the provider's API confirms a live player
 * inside the frame. That handshake is the only evidence available from OUTSIDE
 * a cross-origin iframe that the embed actually WORKS here — a player blocked
 * by the browser (third-party storage partitioned away, EME unavailable, the
 * embed refused outright) loads a frame that simply never answers. The player
 * uses its absence to offer a way out; see the embed health watchdog in
 * CourseViews. Never called more than once, and never after destroy().
 *
 * opts.onUnknown fires instead when the PROVIDER'S SDK could not be loaded or
 * attached at all, which says nothing about the video. That distinction is not
 * academic: cdn.embed.ly is on every common tracker-blocking list, so a large
 * share of members never get player.js — and their video plays perfectly. Only
 * the timestamped-notes feature is lost. Treating that silence as a broken
 * embed would put a warning over a working player for exactly those people, so
 * the watchdog stands down on onUnknown.
 */
export function makeEmbedTimeTracker(iframe, opts = {}) {
    if (!iframe) return null;
    const provider = providerOf(iframe.getAttribute("src"));
    let seconds = null;
    let destroyed = false;
    // One verdict per tracker: ready (a live player answered) or unknown (the
    // SDK never got far enough to ask). Whichever lands first wins, and neither
    // is delivered after destroy().
    let verdictSent = false;
    const markReady = () => {
        if (verdictSent || destroyed) return;
        verdictSent = true;
        try { opts.onReady?.(); } catch { /* ignore */ }
    };
    const markUnknown = () => {
        if (verdictSent || destroyed) return;
        verdictSent = true;
        try { opts.onUnknown?.(); } catch { /* ignore */ }
    };
    let cleanup = () => {};
    let pjsPlayer = null;   // player.js Player (Gumlet), reused for seek
    let vimeoPlayer = null; // Vimeo SDK Player, reused for seek
    let vdoVideo = null;    // VdoCipher media element (DRM), reused for seek

    if (provider === "youtube") {
        loadYouTubeApi()
            .then((YT) => {
                if (destroyed || !YT || !YT.Player) return markUnknown();
                let player;
                try {
                    player = new YT.Player(iframe, { events: { onReady: markReady } });
                } catch { return markUnknown(); }
                const poll = setInterval(() => {
                    try { if (player.getCurrentTime) seconds = player.getCurrentTime(); } catch { /* ignore */ }
                }, 1000);
                cleanup = () => {
                    clearInterval(poll);
                    try { player.destroy && player.destroy(); } catch { /* ignore */ }
                };
            })
            .catch(markUnknown);
    } else if (provider === "vdocipher") {
        // VdoCipher DRM — getInstance(iframe) yields a player whose `.video` is a
        // media element; read currentTime on timeupdate/play (as the old app did).
        loadVdo()
            .then((Vdo) => {
                if (destroyed || !Vdo || !Vdo.getInstance) return markUnknown();
                let player;
                try { player = Vdo.getInstance(iframe); } catch { return markUnknown(); }
                const bind = (p) => {
                    const v = p && p.video;
                    if (!v) return markUnknown();
                    vdoVideo = v;
                    markReady();
                    const onT = () => { try { seconds = v.currentTime; } catch { /* ignore */ } };
                    v.addEventListener("timeupdate", onT);
                    v.addEventListener("play", onT);
                    cleanup = () => { v.removeEventListener("timeupdate", onT); v.removeEventListener("play", onT); };
                };
                if (player && typeof player.then === "function") player.then(bind).catch(markUnknown);
                else bind(player);
            })
            .catch(markUnknown);
    } else if (provider === "vimeo") {
        // Vimeo — its official Player SDK (window.Vimeo.Player). Attach to the
        // existing iframe and read seconds off timeupdate; player.js can't drive
        // Vimeo reliably, so it gets its own SDK (matching the old app).
        loadVimeo()
            .then((V) => {
                if (destroyed || !V || !V.Player) return markUnknown();
                let player;
                try { player = new V.Player(iframe); } catch { return markUnknown(); }
                vimeoPlayer = player;
                try { player.ready().then(markReady).catch(markUnknown); } catch { markUnknown(); }
                const onT = (data) => { if (data && typeof data.seconds === "number") seconds = data.seconds; };
                try { player.on("timeupdate", onT); } catch { /* ignore */ }
                cleanup = () => { try { player.off("timeupdate", onT); } catch { /* ignore */ } };
            })
            .catch(markUnknown);
    } else {
        // Gumlet / flexi_gumlet — via the Embedly player.js SDK, mirroring the
        // legacy GumletVdoplayer: load player.js, wrap the iframe, and subscribe
        // to "timeupdate" INSIDE "ready" (the handshake the provider expects).
        // `seconds` holds the last reported position, kept while paused — exactly
        // what a note wants.
        loadPlayerjs()
            .then((PJS) => {
                // player.js absent = the SDK was blocked (cdn.embed.ly is on the
                // common blocklists). Nothing is known about the video itself.
                if (destroyed || !PJS || !PJS.Player) return markUnknown();
                let player;
                try { player = new PJS.Player(iframe); } catch { return markUnknown(); }
                pjsPlayer = player;
                let subscribed = false;
                const subscribe = () => {
                    if (subscribed) return;
                    subscribed = true;
                    try {
                        player.on("timeupdate", (e) => {
                            // A timeupdate is itself proof of a live player, and
                            // it is the one signal every player.js receiver
                            // sends — some skip "ready" when the frame was
                            // already loaded before player.js attached.
                            markReady();
                            if (e && typeof e.seconds === "number") seconds = e.seconds;
                        });
                    } catch { /* ignore */ }
                };
                // Subscribe on ready; also try immediately in case it's already
                // ready by the time player.js attaches. Only the "ready" event
                // counts as the liveness handshake — the optimistic call below
                // happens whether or not anything is listening inside the frame,
                // so treating it as ready would make the watchdog useless.
                try { player.on("ready", () => { markReady(); subscribe(); }); } catch { /* ignore */ }
                subscribe();
                cleanup = () => {
                    try { player.off("timeupdate"); } catch { /* ignore */ }
                    try { player.off("ready"); } catch { /* ignore */ }
                };
            })
            .catch(markUnknown);
    }

    return {
        getSeconds: () => (typeof seconds === "number" ? seconds : null),
        seekTo: (secs) => {
            const t = Math.max(0, Number(secs) || 0);
            if (provider === "youtube") {
                try {
                    iframe.contentWindow?.postMessage(
                        JSON.stringify({ event: "command", func: "seekTo", args: [t, true] }),
                        "*",
                    );
                } catch { /* ignore */ }
            } else if (provider === "vdocipher") {
                try { if (vdoVideo) vdoVideo.currentTime = t; } catch { /* ignore */ }
            } else if (provider === "vimeo") {
                try { vimeoPlayer?.setCurrentTime?.(t); } catch { /* ignore */ }
            } else {
                try { pjsPlayer?.setCurrentTime?.(t); } catch { /* ignore */ }
            }
        },
        destroy: () => { destroyed = true; cleanup(); },
    };
}
